import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { DatePicker, Input, Select, Space, Table, Typography } from 'antd';
// Api
import { GET_ADMIN_TICKET_ORDERS } from 'api/ticketing/queries';
// Types
import {
  GetAdminTicketOrders,
  GetAdminTicketOrdersVariables,
} from 'api/ticketing/types/GetAdminTicketOrders';
import { AdminTicketOrderRowFields } from 'api/ticketing/types/AdminTicketOrderRowFields';
import {
  TicketEventOrderSort,
  TicketOrderKind,
  TicketOrderStatus,
} from 'api/graphql-global-types';
// Hooks
import { useServerTable } from 'pages/Events/hooks/useServerTable';
// Helpers
import {
  formatEventDateTime,
  ORDER_SORT_COLUMNS,
  ORDER_STATUS_FILTER_OPTIONS,
  pluralize,
} from 'pages/Events/helpers';
// Constants
import { TICKET_EVENT_DETAILS_GEN } from 'constants/routes';
import { EventDetailsTab, EVENTS_PAGE_SIZE } from 'pages/Events/constants';
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import OrderDetails from 'pages/Events/components/OrderDetails/OrderDetails';
import {
  OrderCustomer,
  OrderPayment,
  OrderReference,
  OrderStatus,
  OrderTickets,
} from 'pages/Events/components/OrderCells/OrderCells';
// Styles
import styles from './AllOrders.module.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

type AdminOrderRow = AdminTicketOrderRowFields;

type DateRange = [moment.Moment | null, moment.Moment | null] | null;

const ALL_KINDS = 'all';

const KIND_OPTIONS = [
  { value: ALL_KINDS, label: 'All kinds' },
  { value: TicketOrderKind.Purchase, label: 'Purchases' },
  { value: TicketOrderKind.Comp, label: 'Comps' },
];

const AllOrders = (): JSX.Element => {
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<TicketOrderStatus[]>([]);
  const [kind, setKind] = useState<TicketOrderKind | typeof ALL_KINDS>(
    ALL_KINDS
  );
  const [range, setRange] = useState<DateRange>(null);
  const table = useServerTable({
    sortColumns: ORDER_SORT_COLUMNS,
    defaultSortBy: TicketEventOrderSort.CreatedAt,
    defaultPageSize: EVENTS_PAGE_SIZE,
  });
  const [debouncedSearch] = useDebounce(search.trim(), 400);

  const [from, to] = range || [null, null];

  const { data, loading, error, refetch } = useQuery<
    GetAdminTicketOrders,
    GetAdminTicketOrdersVariables
  >(GET_ADMIN_TICKET_ORDERS, {
    variables: {
      input: {
        searchTerm: debouncedSearch || null,
        statuses: statuses.length ? statuses : null,
        kinds: kind === ALL_KINDS ? null : [kind],
        createdFrom: from ? from.clone().startOf('day').toISOString() : null,
        createdTo: to ? to.clone().endOf('day').toISOString() : null,
        sortBy: table.sortBy,
        direction: table.direction,
        offset: table.offset,
        limit: table.pageSize,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const orders = data?.adminTicketOrders.entities ?? [];
  const total = data?.adminTicketOrders.total ?? 0;
  const hasFilters = Boolean(
    debouncedSearch || statuses.length || kind !== ALL_KINDS || range
  );

  const columns = [
    {
      title: 'Order',
      key: 'reference',
      sorter: true,
      sortOrder: table.sortOrder('reference'),
      render: (_: unknown, row: AdminOrderRow) => (
        <OrderReference order={row} />
      ),
    },
    {
      title: 'Event',
      key: 'event',
      render: (_: unknown, row: AdminOrderRow) => (
        <div className={styles.stack}>
          <Link
            to={`${TICKET_EVENT_DETAILS_GEN}/${row.ticketEvent.id}/${EventDetailsTab.Orders}`}
          >
            {row.ticketEvent.name}
          </Link>
          <Text type="secondary" className={styles.small}>
            {formatEventDateTime(
              row.ticketEvent.startsAt,
              row.ticketEvent.timezone
            )}
          </Text>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      sorter: true,
      sortOrder: table.sortOrder('customer'),
      render: (_: unknown, row: AdminOrderRow) => (
        <OrderCustomer order={row} linkToBuyer />
      ),
    },
    {
      title: 'Tickets',
      key: 'tickets',
      sorter: true,
      sortOrder: table.sortOrder('tickets'),
      render: (_: unknown, row: AdminOrderRow) => <OrderTickets order={row} />,
    },
    {
      title: 'Paid',
      key: 'amount',
      sorter: true,
      sortOrder: table.sortOrder('amount'),
      render: (_: unknown, row: AdminOrderRow) =>
        row.kind === TicketOrderKind.Comp ? (
          'Comp'
        ) : (
          <OrderPayment order={row} />
        ),
    },
    {
      title: 'Date',
      key: 'createdAt',
      sorter: true,
      sortOrder: table.sortOrder('createdAt'),
      render: (_: unknown, row: AdminOrderRow) =>
        formatEventDateTime(row.createdAt, row.ticketEvent.timezone),
    },
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      sortOrder: table.sortOrder('status'),
      render: (_: unknown, row: AdminOrderRow) => (
        <OrderStatus order={row} timezone={row.ticketEvent.timezone} />
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <PageToolbar
        leading={
          <Space wrap>
            <Input.Search
              allowClear
              aria-label="Search orders"
              placeholder="Order number, email or name"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                table.setPage(1);
              }}
              className={styles.search}
            />
            <Select<TicketOrderStatus[]>
              mode="multiple"
              allowClear
              aria-label="Filter by status"
              placeholder="All except abandoned checkouts"
              value={statuses}
              options={ORDER_STATUS_FILTER_OPTIONS}
              onChange={(value) => {
                setStatuses(value);
                table.setPage(1);
              }}
              className={styles.statusFilter}
            />
          </Space>
        }
      >
        <Space wrap>
          <Select<TicketOrderKind | typeof ALL_KINDS>
            aria-label="Filter by kind"
            value={kind}
            options={KIND_OPTIONS}
            onChange={(value) => {
              setKind(value);
              table.setPage(1);
            }}
            className={styles.kindFilter}
          />
          <RangePicker
            allowClear
            value={range}
            onChange={(value) => {
              setRange(value);
              table.setPage(1);
            }}
          />
          {/* Orders span many venues, so the range is whole days where the admin is */}
          <Text type="secondary" className={styles.small}>
            Dates in your time zone ({momentTimezone.tz.guess()})
          </Text>
        </Space>
      </PageToolbar>

      {error ? (
        <LoadErrorAlert
          message="Failed to load the orders"
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Table<AdminOrderRow>
          rowKey="orderId"
          columns={columns}
          dataSource={orders}
          loading={loading && !data}
          onChange={table.onChange}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: hasFilters ? 'No orders match.' : 'No orders yet.',
          }}
          expandable={{
            expandedRowRender: (row) => (
              <OrderDetails
                order={row}
                timezone={row.ticketEvent.timezone}
                showPromoCode
              />
            ),
          }}
          pagination={{
            current: table.page,
            pageSize: table.pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => pluralize(count, 'order'),
          }}
        />
      )}
    </div>
  );
};

export default AllOrders;
